import { getScenesByGroup, tourConfig } from '../data/tour.config';
import type { SceneGroup } from '../data/types';
import { groupLabel, sceneLabel, translate } from '../i18n';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useTourStore } from '../hooks/useTourStore';
import overviewIcon from '../assets/figma/group-overview.svg';
import cihIcon from '../assets/figma/group-cih.png';
import giaAnIcon from '../assets/figma/group-gia-an.png';
import styles from './SceneList.module.css';

const groupIcons: Record<SceneGroup, { src: string; width: number; height: number }> = {
  overview: { src: overviewIcon, width: 23.9796, height: 24 },
  cih: { src: cihIcon, width: 24.664, height: 24.34 },
  'gia-an': { src: giaAnIcon, width: 24.676, height: 24.351 },
};

export function SceneList() {
  const locale = useTourStore((s) => s.locale);
  const currentSceneId = useTourStore((s) => s.currentSceneId);
  const isOpen = useTourStore((s) => s.isSceneListOpen);
  const goToScene = useTourStore((s) => s.goToScene);
  const setSceneListOpen = useTourStore((s) => s.setSceneListOpen);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const handleSelect = (sceneId: string) => {
    goToScene(sceneId);
    if (!isDesktop) {
      setSceneListOpen(false);
    }
  };

  const sortedGroups = [...tourConfig.groups].sort((a, b) => a.order - b.order);

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className={styles.backdrop}
          aria-label={translate(locale, 'app.close')}
          onClick={() => setSceneListOpen(false)}
        />
      )}

      <nav
        className={`${styles.panel} ${!isOpen ? styles.panelClosed : ''}`}
        aria-label={translate(locale, 'app.sceneList')}
        aria-hidden={!isOpen}
      >
        {sortedGroups.map((group) => {
          const scenes = getScenesByGroup(group.id);
          const icon = groupIcons[group.id];

          return (
            <section key={group.id} className={styles.group}>
              <h2 className={styles.groupHeader}>
                <span className={styles.groupIcon}>
                  <span className={styles.groupIconInner}>
                    <img src={icon.src} width={icon.width} height={icon.height} alt="" />
                  </span>
                </span>
                <span className={styles.groupName}>{groupLabel(locale, group.id)}</span>
              </h2>

              <ul className={styles.sceneList}>
                {scenes.map((scene) => {
                  const isActive = scene.id === currentSceneId;

                  return (
                    <li key={scene.id}>
                      <button
                        type="button"
                        className={`${styles.sceneBtn} ${isActive ? styles.sceneActive : ''}`}
                        onClick={() => handleSelect(scene.id)}
                        aria-current={isActive ? 'true' : undefined}
                      >
                        {sceneLabel(locale, scene.id)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </nav>
    </>
  );
}
